import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AppointmentPanel from "./AppointmentPanel";
import CalendarToolbar from "./CalendarToolbar";
import MonthGrid from "./MonthGrid";
import ScheduleSidebar from "./ScheduleSidebar";
import TimeGrid from "./TimeGrid";
import { getVisibleDays } from "./calendar.utils";
import { ScheduleCalendarProps } from "./ScheduleCalendar.types";
import styles from "./ScheduleCalendar.module.css";

function StateMessage({
  title,
  message,
  actionLabel,
  onAction,
}: {
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className={styles.stateMessage}>
      <span className={styles.stateIcon}>◷</span>
      <h3>{title}</h3>
      {message && <p>{message}</p>}
      {actionLabel && onAction && (
        <button type="button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export default function ScheduleCalendar({
  title,
  subtitle,
  schedules,
  selectedScheduleIds,
  events,
  anchorDate,
  view,
  schedulesLoading = false,
  eventsLoading = false,
  schedulesError,
  eventsError,
  onSelectedScheduleIdsChange,
  onAnchorDateChange,
  onViewChange,
  onRetrySchedules,
  onRetryEvents,
}: ScheduleCalendarProps) {
  const { t } = useTranslation();
  const [selectedEventId, setSelectedEventId] = useState<string>();
  const visibleDays = useMemo(
    () => getVisibleDays(anchorDate, view),
    [anchorDate, view],
  );
  const selectedEvent = events.find((event) => event.id === selectedEventId);

  useEffect(() => {
    if (selectedEventId && !events.some((event) => event.id === selectedEventId)) {
      setSelectedEventId(undefined);
    }
  }, [events, selectedEventId]);

  const calendarContent = () => {
    if (schedulesLoading) {
      return (
        <StateMessage
          title={t("calendar.loadingSchedules")}
          message={t("calendar.loadingSchedulesHint")}
        />
      );
    }

    if (schedulesError) {
      return (
        <StateMessage
          title={t("calendar.scheduleError")}
          message={schedulesError}
          actionLabel={t("common.retry")}
          onAction={onRetrySchedules}
        />
      );
    }

    if (schedules.length === 0) {
      return (
        <StateMessage
          title={t("calendar.noSchedules")}
          message={t("calendar.noSchedulesHint")}
        />
      );
    }

    if (selectedScheduleIds.length === 0) {
      return (
        <StateMessage
          title={t("calendar.noSelection")}
          message={t("calendar.noSelectionHint")}
        />
      );
    }

    return (
      <>
        {eventsError && (
          <div className={styles.inlineError} role="alert">
            <span>{eventsError}</span>
            {onRetryEvents && (
              <button type="button" onClick={onRetryEvents}>
                {t("common.retry")}
              </button>
            )}
          </div>
        )}
        <div className={styles.gridWrapper} aria-busy={eventsLoading}>
          {view === "month" ? (
            <MonthGrid
              anchorDate={anchorDate}
              days={visibleDays}
              events={events}
              schedules={schedules}
              onDayClick={(day) => {
                onAnchorDateChange(day);
                onViewChange("day");
              }}
              onEventClick={(event) => setSelectedEventId(event.id)}
            />
          ) : (
            <TimeGrid
              days={visibleDays}
              events={events}
              schedules={schedules}
              onEventClick={(event) => setSelectedEventId(event.id)}
            />
          )}
          {eventsLoading && (
            <div className={styles.loadingOverlay} role="status">
              <span className={styles.spinner} />
              {t("calendar.loadingAppointments")}
            </div>
          )}
          {!eventsLoading && !eventsError && events.length === 0 && (
            <div className={styles.emptyCalendarNotice}>
              {t("calendar.noAppointments")}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className={`${styles.workspace} schedule-workspace`}>
      <header className={styles.workspaceHeader}>
        <div>
          <span className={styles.eyebrow}>{t("calendar.eyebrow")}</span>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <span className={styles.readOnlyBadge}>{t("calendar.readOnly")}</span>
      </header>

      <CalendarToolbar
        anchorDate={anchorDate}
        view={view}
        onAnchorDateChange={onAnchorDateChange}
        onViewChange={onViewChange}
      />

      <div className={styles.workspaceBody}>
        <ScheduleSidebar
          schedules={schedules}
          selectedScheduleIds={selectedScheduleIds}
          selectedDate={anchorDate}
          onSelectedScheduleIdsChange={onSelectedScheduleIdsChange}
          onSelectDate={onAnchorDateChange}
        />
        <main className={styles.calendarSurface}>{calendarContent()}</main>
        {selectedEvent && (
          <AppointmentPanel
            event={selectedEvent}
            schedules={schedules}
            onClose={() => setSelectedEventId(undefined)}
          />
        )}
      </div>
    </div>
  );
}
